// Mode detection types
export enum SiteMode {
  EARLY_STAGE = 'EARLY_STAGE',
  PERFORMANCE = 'PERFORMANCE'
}

export interface SiteModeData {
  mode: SiteMode;
  reasonCodes: string[];
  eligibleFeatures: string[];
  disabledFeatures: string[];
  siteAgeDays: number;
  gscDataDays: number;
  totalClicks: number;
  totalImpressions: number;
  gscDataStartDate: string | null;
  gscDataEndDate: string | null;
  ga4Connected: boolean;
  totalConversions: number;
  readinessProgress: number;
}

export interface ReadinessPillar {
  pillarId: string;
  pillarName: string;
  status: 'READY' | 'PARTIAL' | 'NOT_READY' | 'UNKNOWN';
  score: number;
  summary: string;
  issues: string[];
  recommendations: string[];
  totalPages?: number;
  pagesWithLinks?: number;
  orphanPages?: number;
  rawMetrics?: Record<string, unknown>;
}

export interface ReadinessAction {
  actionId: string;
  title: string;
  description: string;
  whyItMatters: string;
  effort: 'LOW' | 'MEDIUM' | 'HIGH';
  impact: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  targetUrl?: string;
}

export interface ReadinessData {
  overallScore: number;
  pillars: ReadinessPillar[];
  recommendedActions: ReadinessAction[];
  siteAgeDays: number;
  daysUntilReady: number;
}

// Pillar detail types
export type PillarStatus = 'NOT_READY' | 'NEEDS_WORK' | 'READY' | 'EXCELLENT';

export interface DetailCheck {
  checkName: string;
  status: 'PASS' | 'FAIL' | 'INFO';
  result: string;
  description: string;
  recommendation?: string;
}

export interface DetailAction {
  actionTitle: string;
  description: string;
  effortLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  impactLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  estimatedMinutes: number;
}

export interface PillarIssueGroup {
  scopeId?: string;
  checkId?: string;
  checkName?: string;
  status?: string;
  severity?: string;
  actionType?: string;
  recommendation?: string;
  affectedPageCount?: number;
  sampleUrls?: string[];
}

export interface PillarDetailResponse {
  pillarId: string;
  pillarName: string;
  status: PillarStatus;
  score: number;
  summary: string;
  checks: DetailCheck[];
  whyItMatters: string;
  actions: DetailAction[];
  failedCheckCount?: number;
  warningCheckCount?: number;
  affectedPageCount?: number;
  scoreDelta?: number | null;
  newIssueCount?: number;
  recurringIssueCount?: number;
  resolvedIssueCount?: number;
  worseningIssueCount?: number;
  issueGroups?: PillarIssueGroup[];
  // Site-level raw metrics exposed for pillar-specific pages
  rawMetrics?: Record<string, unknown>;
  issues?: string[];
  recommendations?: string[];
}

// Page-level readiness types
export type ReadinessLevel = 'EXCELLENT' | 'GOOD' | 'NEEDS_WORK' | 'POOR';
export type IntentType = 'INFORMATIONAL' | 'COMMERCIAL' | 'TRANSACTIONAL' | 'NAVIGATIONAL' | 'MIXED' | 'UNKNOWN';
export type CheckStatus = 'PASS' | 'FAIL' | 'WARNING' | 'INFO';

export interface ReadinessScore {
  score: number;
  level: ReadinessLevel;
  summary: string;
}

export interface ReadinessCheck {
  scopeId?: string;
  checkId?: string;
  checkName: string;
  status: CheckStatus;
  severity?: string;
  score?: number;
  issue: string;
  message?: string;
  recommendation: string;
  reasonCode?: string;
  actionType?: string;
  evidence?: Record<string, unknown>;
}

export interface PageReadinessRecommendationDetail {
  checkId?: string;
  scopeId?: string;
  title: string;
  status: CheckStatus;
  severity?: string;
  score?: number;
  reasonCode?: string;
  issue?: string;
  recommendation: string;
  actionType?: string;
  affectedUrl?: string;
  targetUrl?: string;
  evidence?: Record<string, unknown>;
}

export interface PageReadinessScope {
  scopeId: string;
  scopeName: string;
  score: number;
  maxScore: number;
  level: ReadinessLevel;
  summary: string;
  availabilityStatus?: 'not_available' | 'not_applicable';
  metrics?: Record<string, unknown>;
}

export interface TopQuery {
  query: string;
  impressions: number;
  clicks: number;
  avgPosition: number | null;
  intent: IntentType;
}

export interface PageReadinessResponse {
  pageId: number;
  searchEngines?: string[];
  url: string;
  title: string;
  description?: string;
  pageType?: string;
  overallScore: ReadinessScore;
  detectedIntent: IntentType;
  intentConfidence?: number;
  topQueries?: TopQuery[];
  checks: ReadinessCheck[];
  recommendationDetails: PageReadinessRecommendationDetail[];
  actions?: Array<Record<string, unknown>>;
  isIndexed: boolean;
  clicks: number;
  impressions: number;
  
  // AI Searchability score (0–15 raw, not scaled to 100)
  aiSearchabilityScore?: number;
  scopes?: PageReadinessScope[];

  // Enhanced webtools framework data
  indexingDetails?: UrlIndexState;
  keywordStats?: KeywordRankingStats;
  engineMetrics?: EngineMetrics[];
  localBusinessMetrics?: LocalBusinessMetrics;
  keywordOpportunities?: KeywordOpportunity[];
}

// Enhanced data structures for webtools framework
export interface UrlIndexState {
  coverageState: string; // INDEXED, NOT_INDEXED, BLOCKED, etc.
  indexingState: string; // Detailed state from GSC
  robotsTxtState: string; // ALLOWED, BLOCKED
  lastCrawlTime?: string;
  mobileUsabilityState?: string; // MOBILE_FRIENDLY, etc.
  sourceTool?: string; // GSC, BING, UNIFIED
  crawlAnomalies?: string;
  pageFetchState?: string;
}

export interface KeywordRankingStats {
  totalKeywords: number;
  avgPosition: number;
  top3Keywords: number;
  top10Keywords: number;
  top50Keywords: number;
  totalClicks: number;
  totalImpressions: number;
}

export interface EngineMetrics {
  name: string; // GOOGLE, BING
  clicks: number;
  impressions: number;
  avgPosition: number;
  ctr: number;
  keywordCount: number;
}

export interface LocalBusinessMetrics {
  profileViews: number;
  searchViews: number;
  mapsViews: number;
  calls: number;
  directions: number;
  websiteClicks: number;
}

export interface KeywordOpportunity {
  query: string;
  position: number;
  clicks: number;
  impressions: number;
  ctr: number;
  searchAppearance?: string;
  country?: string;
  device?: string;
}

// Mode transition types
export interface TransitionCriteria {
  name: string;
  met: boolean;
  currentValue: string;
  requiredValue: string;
  description: string;
}

export interface ModeTransitionEligibility {
  eligible: boolean;
  forceTransition: boolean;
  gracePeriod: boolean;
  status: 'ELIGIBLE' | 'NOT_ELIGIBLE' | 'GRACE_PERIOD' | 'FORCE_TRANSITION' | 'MANUAL_OVERRIDE';
  message: string;
  criteria: TransitionCriteria[];
  daysUntilEligible: number | null;
}

// Activation metrics types
export enum ActivationEventType {
  SITE_CONNECTED = 'SITE_CONNECTED',
  EARLY_STAGE_DASHBOARD_VIEWED = 'EARLY_STAGE_DASHBOARD_VIEWED',
  READINESS_PILLAR_VIEWED = 'READINESS_PILLAR_VIEWED',
  ACTION_COMPLETED = 'ACTION_COMPLETED',
  ACTIVE_WEEK = 'ACTIVE_WEEK',
  PILLAR_IMPROVEMENT = 'PILLAR_IMPROVEMENT',
  PAGE_READINESS_VIEWED = 'PAGE_READINESS_VIEWED',
  EDUCATION_LINK_CLICKED = 'EDUCATION_LINK_CLICKED',
  TIMELINE_VIEWED = 'TIMELINE_VIEWED',
  GLOSSARY_VIEWED = 'GLOSSARY_VIEWED',
  PAGE_FIXES_REPEATED = 'PAGE_FIXES_REPEATED',
  INTERNAL_LINKS_ADDED = 'INTERNAL_LINKS_ADDED',
  INDEXING_IMPROVED = 'INDEXING_IMPROVED',
  MODE_TRANSITION_UNLOCKED = 'MODE_TRANSITION_UNLOCKED'
}

export interface Day7Activation {
  activated: boolean;
  score: number;
  siteConnected: boolean;
  dashboardViews: number;
  dashboardViewsRequired: number;
  pillarsViewed: number;
  pillarsViewedRequired: number;
  actionsCompleted: number;
  actionsCompletedRequired: number;
  timelineViewed: boolean; // visited the SEO Journey Timeline page
  missingCriteria: string[];
}

export interface Day30Activation {
  activated: boolean;
  score: number;
  activeWeeks: number;
  activeWeeksRequired: number;
  actionsCompleted: number;
  actionsCompletedRequired: number;
  pillarsTouched: number;
  pillarsTouchedRequired: number;
  pillarsImproved: number;
  pillarsImprovedRequired: number;
  missingCriteria: string[];
}

/**
 * Secondary engagement depth signals.
 * These do NOT affect the Day-7 / Day-30 activation score.
 * They help customers see how deeply they are exploring the platform.
 */
export interface EngagementSignals {
  /** Customer has visited the SEO Journey Timeline page */
  timelineViewed: boolean;
  /** How many individual page readiness reports the customer has opened */
  pageReadinessViews: number;
  /** How many educational resource links the customer has clicked */
  educationLinksClicked: number;
  /** Customer applied the same type of fix across 2+ different pages */
  pageFixesRepeated: boolean;
  /** Customer completed at least one internal-linking improvement action */
  internalLinksAdded: boolean;
  /** Site indexing score improved compared to the previous day */
  indexingImproved: boolean;
  /** Site has graduated (or is eligible to graduate) to Performance mode */
  modeTransitionUnlocked: boolean;
}

export interface ActivationMetrics {
  siteAgeDays: number;
  day7: Day7Activation;
  day30: Day30Activation;
  engagementSignals: EngagementSignals;
}
